/**
* @flow
*/

import React, { useEffect, useState, useRef } from 'react';
import Button from '@uniiverse/library/components/button';
import { useOutsideClick } from 'hooks/use-outside-click';
import cx from 'classnames';
import Tooltip from '../tooltip/tooltip.js';
import { menu } from './locale';

// Styles
import './index.css';

const Menu = ({
  intl,
  options,
  buttonType,
  buttonText,
  showArrow,
  showToolTip,
  toolTipMessage,
  toolTipArrowAlign,
  customButton,
  renderIcon,
  customClass,
}) => {
  const [expanded, setExpanded] = useState(false);
  const getClassNames = cx('Menu__selector', {
    expanded,
    closed: !expanded
  });

  const menuEl = useRef();

  useOutsideClick(menuEl, () => expanded && setExpanded(false));

  const closeMenu = (e) => {
    if (!expanded) return;
  
    // IE specific check for the reason commented below
    if (typeof(e.composedPath) !== 'function') {
      if (e.target.parentElement.className !== 'Menu') {
        setExpanded(false);
      }
      return;
    }
  
    // the event object is a PointerEvent on IE
    // and does not have composedPath method but
    // the below line still executes composedPath method
    // and throws an exception => doesn't set state for IE
    const path = e.path || (e.composedPath && e.composedPath());
  
    // browser doesn't support path information (safari, firefox, etc)
    if (!path) {
      setExpanded(false);
      return;
    }
    const isMenuClicked = path.filter(currentPath => {
      return currentPath.className === 'Menu';
    });
  
    if (isMenuClicked.length === 0) {
      setExpanded(false);
    }
  };

  const toolTipClassNames = cx('toolTipText', {
    left: toolTipArrowAlign === 'left',
    center: toolTipArrowAlign === 'center',
    right: toolTipArrowAlign === 'right'
  });

  const menuOptions = options.reduce((filtered, option, index) => {
    if (option.show) {
      const menuOption = (
        <div
          onClick={option.disabled ? null : () => fireCallback(option)}
          className={`Menu__selector__option ${option.disabled ? 'disabled' : ''}`}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          {option.disabled && option.tooltipMessage && (
            <Tooltip top="-60px" left="32px" content={option.tooltipMessage}>
              <a className={option.classNames} href={option.href} target="_blank" rel="noopener noreferrer">
                {option.text}
              </a>
            </Tooltip>
          )}
          {option.firstIcon}
          {!option.disabled &&
            <a className={option.classNames} href={option.href} target="_blank" rel="noopener noreferrer">
              {option.text}
            </a>}
          {option.lastIcon}
        </div>
      );

      filtered.push(menuOption);
    }
    return filtered;
  }, []);

  let toolTip;

  if (showToolTip) {
    toolTip = (
      <span className={toolTipClassNames}>
        <span className="toolTipMessage">{toolTipMessage}</span>
      </span>
    );
  }

  const fireCallback = (option) => {
    option.callback();
    openSelector();
  }

  const openSelector = () => {
    setExpanded(!expanded);
  }

  useEffect(() => {
    window.addEventListener('click', closeMenu.bind(this)); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderArrow = () => {
    if (showArrow) {
      return renderIcon ? renderIcon() : <span className="Menu__arrow-down" />;
    }
    return null;
  }

  const getMenuClass = () => {
    let className = 'Menu';
    
    if (customClass) className += ` ${customClass}`;
    return className;
  }

  const renderButton = () => {
    const isButtonDisabled = menuOptions.length === 0;
    if (customButton) {
      return <div
        className="Open-Button"
        onClick={() => openSelector()}
        disabled={isButtonDisabled}
        role="button"
      >
        {customButton}
      </div>
    }

    return <Button
      onClick={() => openSelector()}
      size="small"
      type={buttonType}
      disabled={isButtonDisabled}
    >
      {buttonText || intl.formatMessage(menu.more)}
      {renderArrow()}
    </Button>
  }

  return (
    <div className={getMenuClass()} ref={menuEl}>
      {toolTip}
      {renderButton()}
      <div className={getClassNames}>{menuOptions}</div>
    </div>
  );
}

export default Menu;
